import { getCatalogFilterOptions, getCatalogItems } from '@/services/catalog';

import type { TCatalogFilterOptionsParams, TGetCatalogItemsQuery } from '@/types/productCatalog';

import { stableParams, unwrapData } from './utils';

/**
 * Keys of the catalog area (Product Catalog page and the Products tab of a station page). Every
 * catalog query starts with `catalogKeys.all`, so a save can mark the whole area old with
 * `queryClient.invalidateQueries(catalogKeys.all)`.
 * Invalidated by (whole `catalogKeys.all`): W4 `useCreateCatalogItem` (`src/mutations/catalog.ts`)
 * and W6 the import polling in `ProductCatalogListView` when the import reaches COMPLETED.
 * W1 (`updateStation`, PR 2) also marks `catalog` old, because the filter options hold site names.
 * W5 `patchCatalogItem` has no hook: nothing in the portal calls it. When a screen starts to edit
 * an item, add a save hook in `src/mutations/catalog.ts` that marks `catalogKeys.all` old.
 * W7 `startCatalogExport` changes no data the portal shows and stays a plain call in the page
 * (it is a polling job, not page data).
 */
export const catalogKeys = {
  all: ['catalog'] as const,
  lists: () => [...catalogKeys.all, 'list'] as const,
  list: (params: TGetCatalogItemsQuery) => [...catalogKeys.lists(), stableParams(params)] as const,
  filterOptions: () => [...catalogKeys.all, 'filterOptions'] as const,
  filterOption: (params: Omit<TCatalogFilterOptionsParams, 'signal'>) =>
    [...catalogKeys.filterOptions(), stableParams(params)] as const,
};

/**
 * Query for ONE numbered page of the Product Catalog table.
 * What: resolves to the whole page answer (`response.data`: `{ message, data: { result, total,
 * totalHits, hasMore } }`), because the page reads both the rows and the total from it.
 * Why: the table keeps no copy of the rows; it reads this saved answer, so coming back shows the
 * page at once and refreshes it quietly.
 * Key: `['catalog', 'list', { page, size, filter, sort }]`. The page number is part of the key, so
 * every page is saved apart. `stableParams` copies the params and sorts the site ids (the server
 * treats them as a set). `filter.allSites` stays in the key: "All sites" and "no site" are
 * different requests.
 * Signal: passed on, so leaving the page or changing the filter cancels a call that is loading.
 * Invalidated by: W4 `createCatalogItem`, W6 import COMPLETED (both `catalogKeys.all`), W1 site rename.
 *
 * @param params Page, size, filter and sort of the request.
 */
export const catalogListQuery = (params: TGetCatalogItemsQuery) => {
  const stable = stableParams(params);

  return {
    queryKey: catalogKeys.list(stable),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      getCatalogItems(stable, signal).then((response) => unwrapData(response.data)),
  };
};

/**
 * Query for the Location or Category options of the catalog filter pills.
 * What: resolves to the list of option strings (`response.data.result`, `[]` when missing).
 * Why: the pills ask once per typed text; the same text opens at once while fresh (app default), and a catalog
 * or site save marks every saved search old.
 * Key: `['catalog', 'filterOptions', { stationId, field, q, category?, shelfLocation? }]`. Site ids are sorted by
 * `stableParams`. The abort `signal` of the picker is NOT part of the key.
 * Cross-filter: when `category` or `shelfLocation` arrays are present, only options matching those values are returned.
 * Different cross-filter selections produce different cache entries.
 * Signal: the one React Query gives the query function is passed on. `fetchQuery` owns it, so a
 * caller that wants "picker closed means cancel" cancels the query itself (see
 * `ProductCatalogHeader`).
 * Invalidated by: W4, W6 (`catalogKeys.all`) and W1 (site names).
 *
 * @param params Sites (omit for "All sites"), field (`CATEGORY` or `SHELF_LOCATION`), typed text, and optional cross-filter arrays.
 */
export const catalogFilterOptionsQuery = (params: Omit<TCatalogFilterOptionsParams, 'signal'>) => {
  const stable = stableParams(params);

  return {
    queryKey: catalogKeys.filterOption(stable),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      getCatalogFilterOptions({ ...stable, signal }).then((response) => response.data.result ?? []),
  };
};
